import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { extractLogsService, diskSpaceService, jobsService, servicesService } from '../services/api';
import { getErrorMessage } from '../utils/errors';
import { formatClockTime, formatWeekdayDate, formatHeartbeat } from '../utils/format';
import { groupFailedJobs } from '../utils/jobFilters';

// Heartbeat names that older Aurora.Missioncontrol builds send from warm-ups and connection tests.
// They aren't real services (they always end up Offline), so they're left off the Command Center.
const NON_SERVICE_HEARTBEATS = ['WarmUp', 'ConnectionTest'];

function CommandCenter() {
    const [diskSpace, setDiskSpace] = useState(null);
    const [services, setServices] = useState([]);
    const [activeJobs, setActiveJobs] = useState([]);
    const [allJobs, setAllJobs] = useState([]);
    const [lastRuns, setLastRuns] = useState([]);
    const [refreshInterval, setRefreshInterval] = useState(30);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [runningExpanded, setRunningExpanded] = useState(false);
    const [idleExpanded, setIdleExpanded] = useState(false);
    const [offlineExpanded, setOfflineExpanded] = useState(false);
    const navigate = useNavigate();

    // Update clock every second
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        loadData();
    }, []);

    // Auto-refresh
    useEffect(() => {
        if (refreshInterval === 0) return;
        const interval = setInterval(loadData, refreshInterval * 1000);
        return () => clearInterval(interval);
    }, [refreshInterval]);

    // Auto-expand running section when services start running
    useEffect(() => {
        const running = services.filter(s => s.status === 'Running' || s.status?.startsWith('Running'));
        if (running.length > 0) {
            setRunningExpanded(true);
        }
    }, [services]);

    const loadData = async () => {
        try {
            const [diskData, svc, active, jobs, runs] = await Promise.all([
                diskSpaceService.getDiskSpace(),
                servicesService.getServices(),
                jobsService.getActiveJobs(),
                jobsService.getJobs(24), // Get last 24 hours of jobs
                extractLogsService.getRuns(1)
            ]);
            setDiskSpace(diskData);
            setServices((svc || []).filter(s => !NON_SERVICE_HEARTBEATS.includes(s.serviceName)));
            setActiveJobs(active || []);
            setAllJobs(jobs || []);
            setLastRuns(runs || []);
            setError(null);
        } catch (err) {
            setError(`Connection error: ${getErrorMessage(err)}`);
        } finally {
            setLoading(false);
        }
    };

    // Calculate alerts
    const criticalDisks = diskSpace?.disks?.filter(d => d.status === 'Critical') || [];
    const warningDisks = diskSpace?.disks?.filter(d => d.status === 'Warning') || [];
    const failedRuns = lastRuns.filter(r => r.status === 'Failed' || r.status === 'Skipped');
    // allJobs covers the last 24 hours, so a failure drops off once it's older than that
    const failedJobGroups = groupFailedJobs(allJobs);

    // Group services by status
    const runningServices = services.filter(s => s.status === 'Running' || s.status?.startsWith('Running'));
    const idleServices = services.filter(s => s.status === 'Idle');
    const offlineServices = services.filter(s => s.status === 'Offline');
    
    const getSystemStatus = () => {
        if (criticalDisks.length > 0 || offlineServices.length > 0 || failedRuns.length > 0 || failedJobGroups.length > 0) return 'critical';
        if (warningDisks.length > 0) return 'warning';
        return 'healthy';
    };

    // Get job counts for a service
    const getJobCounts = (serviceName) => {
        const serviceJobs = allJobs.filter(j => 
            j.jobType?.includes(serviceName) || 
            j.jobType === serviceName
        );
        return {
            running: serviceJobs.filter(j => j.status === 'Running').length,
            complete: serviceJobs.filter(j => j.status === 'Complete').length,
            failed: serviceJobs.filter(j => j.status === 'Failed').length,
            queued: serviceJobs.filter(j => j.status === 'Queued').length
        };
    };

    // Running Service Card (larger with queue watermark)
    const RunningServiceCard = ({ service }) => {
        const job = service.currentJobId ? 
            activeJobs.find(j => j.jobId === service.currentJobId) : null;
        const counts = getJobCounts(service.serviceName);
        const hasFailures = counts.failed > 0;
        const displayStatus = service.status || 'Running';
        
        return (
            <div 
                className={`mc-card-v2 running ${hasFailures ? 'has-failures' : ''}`}
                onClick={() => navigate(`/jobs?service=${encodeURIComponent(service.serviceName)}`)}
            >
                {/* Queue Watermark */}
                {(counts.queued > 0 || counts.running > 0) && (
                    <div className="mc-queue-watermark">{counts.queued + counts.running}</div>
                )}
                
                {/* Content */}
                <div className="mc-card-content">
                    <div className="mc-card-header">
                        <div className="mc-card-title">
                            <span className="mc-status-dot running"></span>
                            <span className="mc-card-name">{service.serviceName}</span>
                        </div>
                        <span className="mc-card-heartbeat">{formatHeartbeat(service.secondsSinceHeartbeat)}</span>
                    </div>
                    
                    <div className="mc-card-machine">{service.machineName || 'Unknown'}</div>
                    
                    {/* Job Counts */}
                    <div className="mc-job-counts">
                        <div className="mc-count complete">
                            <span className="mc-count-value">{counts.complete}</span>
                            <span className="mc-count-label">done</span>
                        </div>
                        <div className={`mc-count failed ${counts.failed > 0 ? 'active' : ''}`}>
                            <span className="mc-count-value">{counts.failed}</span>
                            <span className="mc-count-label">fail</span>
                        </div>
                        <div className={`mc-count running ${counts.running > 0 ? 'active' : ''}`}>
                            <span className="mc-count-value">{counts.running}</span>
                            <span className="mc-count-label">run</span>
                        </div>
                        <div className="mc-count queued">
                            <span className="mc-count-value">{counts.queued}</span>
                            <span className="mc-count-label">queue</span>
                        </div>
                    </div>
                    
                    {/* Status */}
                    <div className="mc-card-status-text running">{displayStatus}</div>
                    
                    {/* Progress Bar */}
                    {job && (
                        <div className="mc-progress-container">
                            <div className="mc-progress-bar">
                                <div 
                                    className="mc-progress-fill"
                                    style={{ width: `${job.stepProgressPercent || 0}%` }}
                                ></div>
                            </div>
                            <span className="mc-progress-percent">{job.stepProgressPercent || 0}%</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Idle Service Card
    const IdleServiceCard = ({ service }) => {
        const counts = getJobCounts(service.serviceName);
        const hasFailures = counts.failed > 0;
        
        return (
            <div 
                className={`mc-card-v2 idle ${hasFailures ? 'has-failures' : ''}`}
                onClick={() => navigate(`/jobs?service=${encodeURIComponent(service.serviceName)}`)}
            >
                {/* Content */}
                <div className="mc-card-content">
                    <div className="mc-card-header">
                        <div className="mc-card-title">
                            <span className="mc-status-dot idle"></span>
                            <span className="mc-card-name">{service.serviceName}</span>
                        </div>
                        <span className="mc-card-heartbeat">{formatHeartbeat(service.secondsSinceHeartbeat)}</span>
                    </div>
                    
                    <div className="mc-card-machine">{service.machineName || 'Unknown'}</div>
                    
                    {/* Job Counts */}
                    <div className="mc-job-counts">
                        <div className="mc-count complete">
                            <span className="mc-count-value">{counts.complete}</span>
                            <span className="mc-count-label">done</span>
                        </div>
                        <div className={`mc-count failed ${counts.failed > 0 ? 'active' : ''}`}>
                            <span className="mc-count-value">{counts.failed}</span>
                            <span className="mc-count-label">fail</span>
                        </div>
                        <div className={`mc-count running ${counts.running > 0 ? 'active' : ''}`}>
                            <span className="mc-count-value">{counts.running}</span>
                            <span className="mc-count-label">run</span>
                        </div>
                        <div className="mc-count queued">
                            <span className="mc-count-value">{counts.queued}</span>
                            <span className="mc-count-label">queue</span>
                        </div>
                    </div>
                    
                    {/* Status */}
                    <div className="mc-card-status-text idle">Idle</div>
                </div>
            </div>
        );
    };

    // Offline Service Card
    const OfflineServiceCard = ({ service }) => {
        const counts = getJobCounts(service.serviceName);
        const hasFailures = counts.failed > 0;
        
        return (
            <div 
                className={`mc-card-v2 offline ${hasFailures ? 'has-failures' : ''}`}
                onClick={() => navigate(`/jobs?service=${encodeURIComponent(service.serviceName)}`)}
            >
                {/* Content */}
                <div className="mc-card-content">
                    <div className="mc-card-header">
                        <div className="mc-card-title">
                            <span className="mc-status-dot offline"></span>
                            <span className="mc-card-name">{service.serviceName}</span>
                        </div>
                        <span className="mc-card-heartbeat">{formatHeartbeat(service.secondsSinceHeartbeat)}</span>
                    </div>
                    
                    <div className="mc-card-machine">{service.machineName || 'Unknown'}</div>
                    
                    {/* Job Counts */}
                    <div className="mc-job-counts">
                        <div className="mc-count complete">
                            <span className="mc-count-value">{counts.complete}</span>
                            <span className="mc-count-label">done</span>
                        </div>
                        <div className={`mc-count failed ${counts.failed > 0 ? 'active' : ''}`}>
                            <span className="mc-count-value">{counts.failed}</span>
                            <span className="mc-count-label">fail</span>
                        </div>
                        <div className={`mc-count running ${counts.running > 0 ? 'active' : ''}`}>
                            <span className="mc-count-value">{counts.running}</span>
                            <span className="mc-count-label">run</span>
                        </div>
                        <div className="mc-count queued">
                            <span className="mc-count-value">{counts.queued}</span>
                            <span className="mc-count-label">queue</span>
                        </div>
                    </div>
                    
                    {/* Status */}
                    <div className="mc-card-status-text offline">Offline</div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="mc-dashboard">
                <div className="mc-loading">
                    <div className="mc-loading-ring"></div>
                    <div>Initializing Mission Control...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="mc-dashboard">
            {/* Header */}
            <header className="mc-header">
                <div className="mc-logo">
                    <img src="/mission-control-logo.svg" alt="Mission Control" />
                </div>
                <div className="mc-clock">
                    <div className="mc-time">{formatClockTime(currentTime)}</div>
                    <div className="mc-date">{formatWeekdayDate(currentTime)}</div>
                </div>
                <div className="mc-controls">
                    <button 
                        className="mc-refresh-btn"
                        onClick={async () => {
                            try {
                                const freshData = await diskSpaceService.refreshDiskSpace();
                                setDiskSpace(freshData);
                                setError(null);
                            } catch (err) {
                                setError(`Disk space refresh failed: ${getErrorMessage(err)}`);
                            }
                        }}
                        title="Refresh disk space"
                    >
                        🔄
                    </button>
                    <select 
                        className="mc-refresh-select"
                        value={refreshInterval} 
                        onChange={(e) => setRefreshInterval(Number(e.target.value))}
                    >
                        <option value={0}>Manual</option>
                        <option value={10}>10s</option>
                        <option value={30}>30s</option>
                        <option value={60}>1m</option>
                    </select>
                    <div className={`mc-status-badge ${getSystemStatus()}`}>
                        <span className="mc-status-pulse"></span>
                        {getSystemStatus() === 'healthy' ? 'All Systems Nominal' : 
                         getSystemStatus() === 'warning' ? 'Warning' : 'Alert'}
                    </div>
                </div>
            </header>

            {/* Alert Banners */}
            <div className="mc-alerts">
                {criticalDisks.length > 0 && (
                    <div className="mc-alert critical">
                        <span className="mc-alert-icon">🚨</span>
                        <span className="mc-alert-text">
                            CRITICAL DISK SPACE: {criticalDisks.map(d => 
                                `${d.server} (${d.driveLetter}:) ${d.percentFree}% free (${d.freeGB?.toFixed(1) || 'N/A'} GB)`
                            ).join(' | ')}
                        </span>
                    </div>
                )}
                {warningDisks.length > 0 && (
                    <div className="mc-alert warning">
                        <span className="mc-alert-icon">⚠️</span>
                        <span className="mc-alert-text">
                            LOW DISK: {warningDisks.map(d => 
                                `${d.server} (${d.driveLetter}:) ${d.percentFree}% free (${d.freeGB?.toFixed(1) || 'N/A'} GB)`
                            ).join(' | ')}
                        </span>
                    </div>
                )}
                {failedRuns.length > 0 && (
                    <div className="mc-alert critical">
                        <span className="mc-alert-icon">❌</span>
                        <span className="mc-alert-text">
                            EXTRACT FAILURES: {failedRuns.map(r => r.facilityID).join(', ')}
                        </span>
                        <Link to="/runs" className="mc-alert-link">View Details →</Link>
                    </div>
                )}
                {failedJobGroups.length > 0 && (
                    <div className="mc-alert critical">
                        <span className="mc-alert-icon">❌</span>
                        <span className="mc-alert-text">
                            FAILED JOBS (24H):{' '}
                            {failedJobGroups.map((g, i) => (
                                <React.Fragment key={g.server}>
                                    {i > 0 && ', '}
                                    <Link
                                        to={`/jobs?server=${encodeURIComponent(g.server)}&status=Failed&hours=24`}
                                        className="mc-alert-inline-link"
                                        title={g.latest?.errorMessage ? `Latest: ${g.latest.errorMessage}` : undefined}
                                    >
                                        {g.server} ({g.count})
                                    </Link>
                                </React.Fragment>
                            ))}
                        </span>
                        <Link to="/jobs?status=Failed&hours=24" className="mc-alert-link">View Jobs →</Link>
                    </div>
                )}
                {offlineServices.length > 0 && (
                    <div className="mc-alert offline clickable" onClick={() => setOfflineExpanded(!offlineExpanded)}>
                        <span className="mc-alert-icon">🔴</span>
                        <span className="mc-alert-text">
                            OFFLINE: {offlineServices.map(s => 
                                `${s.serviceName} (${formatHeartbeat(s.secondsSinceHeartbeat)})`
                            ).join(', ')}
                        </span>
                        <span className="mc-alert-link">View Details →</span>
                    </div>
                )}
                {error && (
                    <div className="mc-alert warning">
                        <span className="mc-alert-icon">⚠️</span>
                        <span className="mc-alert-text">{error}</span>
                    </div>
                )}
            </div>

            {/* Services Grouped by Status */}
            <div className="mc-services-grouped">
                {/* Offline Section - Collapsible */}
                <div className="mc-service-section">
                    <div 
                        className="mc-section-header clickable"
                        onClick={() => setOfflineExpanded(!offlineExpanded)}
                    >
                        <span className="mc-section-dot offline"></span>
                        <h2>Offline ({offlineServices.length})</h2>
                        <span className={`mc-section-toggle ${offlineExpanded ? 'expanded' : ''}`}>▶</span>
                    </div>
                    {offlineExpanded && (
                        offlineServices.length > 0 ? (
                            <div className="mc-service-grid offline">
                                {offlineServices.map(service => (
                                    <OfflineServiceCard key={service.serviceId} service={service} />
                                ))}
                            </div>
                        ) : (
                            <div className="mc-section-empty">All services online</div>
                        )
                    )}
                </div>

                {/* Running Section - Collapsible, auto-expands when services running */}
                <div className="mc-service-section">
                    <div 
                        className="mc-section-header clickable"
                        onClick={() => setRunningExpanded(!runningExpanded)}
                    >
                        <span className="mc-section-dot running"></span>
                        <h2>Running ({runningServices.length})</h2>
                        <span className={`mc-section-toggle ${runningExpanded ? 'expanded' : ''}`}>▶</span>
                    </div>
                    {runningExpanded && (
                        runningServices.length > 0 ? (
                            <div className="mc-service-grid running">
                                {runningServices.map(service => (
                                    <RunningServiceCard key={service.serviceId} service={service} />
                                ))}
                            </div>
                        ) : (
                            <div className="mc-section-empty">No services running</div>
                        )
                    )}
                </div>

                {/* Idle Section - Collapsible */}
                <div className="mc-service-section">
                    <div 
                        className="mc-section-header clickable"
                        onClick={() => setIdleExpanded(!idleExpanded)}
                    >
                        <span className="mc-section-dot idle"></span>
                        <h2>Idle ({idleServices.length})</h2>
                        <span className={`mc-section-toggle ${idleExpanded ? 'expanded' : ''}`}>▶</span>
                    </div>
                    {idleExpanded && (
                        idleServices.length > 0 ? (
                            <div className="mc-service-grid idle">
                                {idleServices.map(service => (
                                    <IdleServiceCard key={service.serviceId} service={service} />
                                ))}
                            </div>
                        ) : (
                            <div className="mc-section-empty">No idle services</div>
                        )
                    )}
                </div>

                {/* No Services */}
                {services.length === 0 && (
                    <div className="mc-no-services">
                        <div className="mc-no-services-icon">📡</div>
                        <div className="mc-no-services-text">No import services registered</div>
                        <div className="mc-no-services-hint">Services will appear when they send their first heartbeat</div>
                    </div>
                )}
            </div>

            {/* Quick Stats Bar */}
            <div className="mc-stats-bar">
                <div className="mc-stat">
                    <span className="mc-stat-value">{services.filter(s => s.status !== 'Offline').length}</span>
                    <span className="mc-stat-label">Services Online</span>
                </div>
                <div className="mc-stat">
                    <span className="mc-stat-value">{activeJobs.length}</span>
                    <span className="mc-stat-label">Active Jobs</span>
                </div>
                <div className="mc-stat">
                    <span className="mc-stat-value">{diskSpace?.disks?.length || 0}</span>
                    <span className="mc-stat-label">Disks Monitored</span>
                </div>
                <div className="mc-stat-links">
                    <Link to="/jobs" className="mc-stat-link">View All Jobs →</Link>
                    <Link to="/runs" className="mc-stat-link">View Extracts →</Link>
                    <Link to="/analytics" className="mc-stat-link">Analytics →</Link>
                </div>
            </div>
        </div>
    );
}

export default CommandCenter;
